import type { Directive, DirectiveBinding } from 'vue'

import { watch } from 'vue'

import type { Action, Subjects } from '../generated/abilities.generated'
import type { RbacController } from '../types/runtime'

import { runGates } from '../gates/runGates'

/**
 * Either the `'action:subject'` shorthand or the explicit pair. The shorthand is
 * the common form; the object form exists because a dotted subject built at
 * runtime reads better unsplit.
 */
export type VCanValue = `${Action}:${Subjects}` | { action: Action; subject: Subjects }

const parse = (value: VCanValue): { action: Action; subject: Subjects } | null => {
  if (typeof value !== 'string') {
    return value
  }

  // Split on the FIRST colon only: the action never contains one, and this keeps
  // a subject free to hold anything the convention later allows.
  const boundary = value.indexOf(':')

  if (boundary < 1 || boundary === value.length - 1) {
    return null
  }

  return {
    action: value.slice(0, boundary) as Action,
    subject: value.slice(boundary + 1) as Subjects
  }
}

/**
 * Every press reaches an element as one of these: a mouse press, Enter on a
 * link or Space on a button is a `click`, and a middle press is an `auxclick`.
 */
const ACTIVATION_EVENTS = ['click', 'auxclick'] as const

/**
 * Refuses a press on a denied `.disable` element, wherever inside it the press
 * lands. `disabled` stops a button and nothing else: a link ignores it, so a
 * denied link still navigated, a router link included. Registered for the
 * capture phase, so it runs before every handler on the element or inside it.
 */
const refuse = (event: Event): void => {
  event.preventDefault()
  event.stopImmediatePropagation()
}

const block = (el: HTMLElement): void =>
  ACTIVATION_EVENTS.forEach(type => el.addEventListener(type, refuse, { capture: true }))

const release = (el: HTMLElement): void =>
  ACTIVATION_EVENTS.forEach(type => el.removeEventListener(type, refuse, { capture: true }))

/**
 * Factory for the `v-can` directive, bound to a controller (directives cannot
 * `inject`). Registered by `createRbac` as `v-can`. It is a UX affordance, not a
 * security boundary — the backend's 403 is.
 *
 *   <button v-can="'delete:pos.order'">Delete</button>
 *   <button v-can.disable="'delete:pos.order'">Delete</button>
 *
 * Default hides the element; `.disable` keeps it, refuses every press on it, and
 * sets disabled + aria-disabled + data-rbac-denied. Re-evaluates on
 * ability/connectivity change.
 *
 * A malformed value denies rather than throwing: a directive is not a place to
 * take a page down, and denying is the fail-closed direction.
 */
export const createVCan = (controller: RbacController): Directive<HTMLElement, VCanValue> => {
  const isAllowed = (binding: DirectiveBinding<VCanValue>): boolean => {
    if (controller.status.value !== 'ready') {
      return false
    }

    const check = parse(binding.value)

    if (!check) {
      return false
    }

    const gated = runGates(controller.gates, {
      action: check.action,
      subject: check.subject,
      ability: controller.ability,
      meta: controller.meta.value,
      online: controller.online.value
    })

    if (gated !== undefined) {
      return gated
    }

    return controller.ability.can(check.action, check.subject)
  }

  const apply = (el: HTMLElement, binding: DirectiveBinding<VCanValue>): void => {
    const allowed = isAllowed(binding)

    if (binding.modifiers.disable) {
      el.toggleAttribute('disabled', !allowed)

      if (allowed) {
        release(el)
        el.removeAttribute('aria-disabled')
        el.removeAttribute('data-rbac-denied')
        return
      }

      block(el)
      el.setAttribute('aria-disabled', 'true')
      el.setAttribute('data-rbac-denied', 'true')
      return
    }

    el.style.display = allowed ? '' : 'none'
  }

  const stoppers = new WeakMap<HTMLElement, () => void>()

  // The binding the watcher should re-evaluate, read at fire time rather than
  // captured on mount: `v-can`'s value can change (`v-can="deleteCheck"`), and a
  // watcher closed over the mount-time binding would keep re-applying the check
  // the element no longer declares.
  const bindings = new WeakMap<HTMLElement, DirectiveBinding<VCanValue>>()

  return {
    mounted(el, binding) {
      bindings.set(el, binding)
      apply(el, binding)

      const stop = watch(
        [() => controller.revision.value, () => controller.online.value, () => controller.status.value],
        () => {
          const current = bindings.get(el)

          if (current) {
            apply(el, current)
          }
        }
      )

      stoppers.set(el, stop)
    },
    updated(el, binding) {
      bindings.set(el, binding)
      apply(el, binding)
    },
    unmounted(el) {
      stoppers.get(el)?.()
      stoppers.delete(el)
      bindings.delete(el)
      release(el)
    }
  }
}
